import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BsaCalcApp from "./BsaCalcApp";
import CrcCalcApp from "./CrcCalcApp";
import IbwCalcApp from "./IbwCalcApp";
import LbwCalcApp from "./LbwCalcApp";
import GfrCalcApp from "./GfrCalcApp";
import AbxCalcApp from "./AbxCalcApp";
import { updateState } from "../../features/counter/counterSlice";
import {
  ModernWebLayout,
  PageSidebar,
  MainContent,
} from "../../components/Layout";
import CalculatorModal from "../../components/calculators/CalculatorModal";
import useAppModernisation from "../../hooks/useAppModernisation";
import useCalculatorModal from "../../hooks/modalHooks/useCalculatorModal";
import useScreenSize from "../../hooks/useScreenSize";
import { getCalculatorMenuItems } from "../../constants/calculators";
import styles from "./ModernCalculators.module.scss";

const ModernCalculators = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPath = location.pathname.split("/")[2] || "";
  const { isCalculatorsLegacyIntegration } = useAppModernisation();
  const { modalState, shouldUseModal, openCalculator, closeModal } =
    useCalculatorModal();
  const { isDesktop } = useScreenSize();
  const calculatorContentRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Conditional sidebar visibility:
  // - Drop 2 (isCalculatorsLegacyIntegration): Always show sidebar
  // - Final phase: Hide sidebar when a specific calculator is selected, show on main page
  const showSidebar = isCalculatorsLegacyIntegration ? true : !currentPath;

  useEffect(() => {
    dispatch(
      updateState({
        ditaPath: "",
        headTitle: "",
      }),
    );
  }, [dispatch]);

  // Auto-scroll to calculator content on mobile when calculator integration is enabled
  useEffect(() => {
    if (
      currentPath &&
      isCalculatorsLegacyIntegration &&
      calculatorContentRef.current
    ) {
      if (!isDesktop) {
        const timer = setTimeout(() => {
          calculatorContentRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [currentPath, isCalculatorsLegacyIntegration, isDesktop]);

  // Get calculator menu items from centralized config
  const calculatorMenuItems = getCalculatorMenuItems();

  // Create menu items with modal-aware click handlers
  const createMenuItemsWithModalHandlers = (items) => {
    return items.map((item) => ({
      ...item,
      onClick: shouldUseModal
        ? (e) => {
            e.preventDefault();
            openCalculator(item.id, navigate);
          }
        : undefined,
    }));
  };

  const calculatorMenuItemsWithHandlers =
    createMenuItemsWithModalHandlers(calculatorMenuItems);

  // Determine if sidebar should be closable
  const isOnCalculatorPages = location.pathname.startsWith("/calculator");
  const isClosableSidebar = shouldUseModal && !isOnCalculatorPages;

  const pageSidebarComponent = (
    <PageSidebar
      title="Calculators"
      icon="tg-icons-Calculator"
      menuItems={calculatorMenuItemsWithHandlers}
      defaultActiveId={currentPath || null}
      isClosable={isClosableSidebar}
      onClose={isClosableSidebar ? () => setSidebarOpen(false) : null}
    />
  );

  return (
    <ModernWebLayout
      title="Calculators - Therapeutic Guidelines"
      pageSidebar={showSidebar && sidebarOpen ? pageSidebarComponent : null}
    >
      <MainContent className={styles.calculatorContent} padding="default">
        <div ref={calculatorContentRef}>
          {/* Show specific calculators when selected */}
          {currentPath === "ccbwcalc" && (
            <CrcCalcApp isWebLayout={true} data-testid="ccbw-calc" />
          )}
          {currentPath === "bsacalc" && (
            <BsaCalcApp isWebLayout={true} data-testid="bsa-calc" />
          )}
          {currentPath === "ibwcalc" && (
            <IbwCalcApp isWebLayout={true} data-testid="ibw-calc" />
          )}
          {currentPath === "lbwcalc" && (
            <LbwCalcApp isWebLayout={true} data-testid="lbw-calc" />
          )}
          {currentPath === "gfrcalc" && (
            <GfrCalcApp isWebLayout={true} data-testid="gfr-calc" />
          )}
          {currentPath === "idcalc" && (
            <AbxCalcApp isWebLayout={true} data-testid="id-calc" />
          )}
          {["Amikacin", "Gentamicin", "Tobramycin"].includes(currentPath) && (
            <AbxCalcApp
              currentPath={currentPath}
              isWebLayout={true}
              data-testid="id-calc"
            />
          )}
        </div>
      </MainContent>

      {/* Calculator Modal */}
      <CalculatorModal
        calculatorId={modalState.calculatorId}
        isOpen={modalState.isOpen}
        onClose={closeModal}
      />
    </ModernWebLayout>
  );
};

export default ModernCalculators;
